/**
 * Módulo centralizado de analytics para GTM/GA4
 * 
 * BUENAS PRÁCTICAS:
 * - Todas las llamadas a tracking deben ir a través de estas funciones
 * - NO usar dataLayer.push() directamente
 * - Los valores de module, section, element_name, action y element_type deben ser strings estables y predefinidos
 * - NO incluir IDs, timestamps ni textos dinámicos en estos valores
 * - El userId se expone en window.polkoUserId y GTM lo mapea automáticamente
 * 
 * NOTA: trackUIInteraction usa automáticamente el section del AnalyticsContext si no se proporciona explícitamente
 */

// Importación lazy del contexto para evitar dependencias circulares
let getAnalyticsContext = null;

// Función para obtener el contexto de analytics (se establece desde el provider)
export const setAnalyticsContextGetter = (getter) => {
  getAnalyticsContext = getter;
};

const ensureDataLayer = () => {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * Establece el user_id del usuario autenticado en window.polkoUserId
 * GTM leerá este valor y lo mapeará al campo user_id de GA4
 * 
 * @param {string|null} userId - ID del usuario autenticado, o null para limpiar
 */
export const setPolkoUserId = (userId) => {
  if (typeof window === 'undefined') return;

  if (userId) {
    window.polkoUserId = userId;
  } else {
    delete window.polkoUserId;
  }
};

/**
 * Evento estándar para indicar que el usuario está listo (autenticado o sesión restaurada)
 * 
 * Este evento:
 * - Envía un evento 'user_ready' al dataLayer con user_id y user_role
 * 
 * Se debe llamar:
 * - Cuando el usuario se autentica por primera vez
 * - Cuando se recarga la app y se recupera la sesión desde el backend
 * 
 * @param {string} userId - ID del usuario autenticado
 * @param {string|null} userRole - Rol del usuario ('master', 'organic', 'polkista') o null
 */
export const pushUserReady = (userId, userRole = null) => {
  const dl = ensureDataLayer();
  if (!dl) return;

  if (!userId || typeof userId !== 'string') {
    return;
  }

  // Exponer en window.* para GTM / GA4
  setPolkoUserId(userId);
  setPolkoUserRole(userRole);

  const event = {
    event: 'user_ready',
    user_id: userId
  };

  if (userRole && typeof userRole === 'string') {
    event.user_role = userRole;
  }

  dl.push(event);
};

/**
 * Calcula el rol del usuario basándose en isMaster e idMaster
 * 
 * @param {boolean} isMaster - Si el usuario tiene rol master
 * @param {number|null|undefined} idMaster - ID del master del usuario
 * @returns {string|null} - 'master', 'organic', 'polkista', o null si no se puede determinar
 */
export const calculateUserRole = (isMaster, idMaster) => {
  // Si es master, siempre retornar 'master'
  if (isMaster === true) {
    return 'master';
  }
  
  // Si tiene idMaster, determinar si es orgánico o polkista
  if (idMaster !== null && idMaster !== undefined) {
    if (idMaster === 21) {
      return 'organic';
    } else {
      return 'polkista';
    }
  }
  
  // Si no se puede determinar, retornar null
  return null;
};

/**
 * Establece el user_role del usuario en window.polkoUserRole
 * GTM leerá este valor y lo mapeará al campo user_role de GA4
 * 
 * @param {string|null} userRole - Rol del usuario ('master', 'organic', 'polkista'), o null para limpiar
 */
export const setPolkoUserRole = (userRole) => {
  if (typeof window === 'undefined') return;

  if (userRole) {
    window.polkoUserRole = userRole;
  } else {
    delete window.polkoUserRole;
  }
};

/**
 * Trackea un page view en la SPA
 * Envía evento 'polko_page_view' que GTM convertirá a 'page_view' de GA4
 * 
 * @param {Object} params - Parámetros del page view
 * @param {string} params.module - Módulo de la aplicación (ej: 'dashboard', 'cotizacion_automotor', 'cotizacion_hogar')
 * @param {string} [params.section] - Sección dentro del módulo (ej: 'quotation_form', 'quotation_response_success')
 * @param {string} [params.pageTitle] - Título de la página (opcional, usa document.title por defecto)
 */
export const trackPageView = ({ module, section, pageTitle = null }) => {
  const dl = ensureDataLayer();
  if (!dl) return;

  if (!module || typeof module !== 'string') {
    console.warn('[Analytics] trackPageView: module debe ser un string no vacío');
    return;
  }

  const finalPageTitle = pageTitle || (typeof document !== 'undefined' ? document.title || '' : '');

  const event = {
    event: 'polko_page_view',
    module,
    page_title: finalPageTitle,
    page_path: window.location?.pathname || '',
    page_location: window.location?.href || '',
  };

  if (section && typeof section === 'string') {
    event.section = section;
  }

  // Incluir user_role desde window.polkoUserRole si está disponible
  if (window.polkoUserRole && typeof window.polkoUserRole === 'string') {
    event.user_role = window.polkoUserRole;
  }

  dl.push(event);
};

/**
 * Trackea una interacción de UI (clicks, etc.)
 * Envía evento 'ui_interaction' que GTM enviará a GA4
 * 
 * IMPORTANTE: Si no se proporcionan `module` o `section`, se usan automáticamente del contexto
 * (establecido por usePageViewTracker). Esto permite override explícito cuando un componente
 * necesita algo especial (ej: COMPANY_SELECTOR para clicks en modal de selector).
 * 
 * @param {Object} params - Parámetros de la interacción
 * @param {string} [params.module] - Módulo de la aplicación. Si no se proporciona, usa el del contexto
 * @param {string} [params.section] - Sección dentro del módulo. Si no se proporciona, usa la del contexto
 * @param {string} params.elementName - Nombre del elemento interactuado (ej: 'submit_button', 'filter_dropdown')
 * @param {string} params.action - Acción realizada (ej: 'click', 'change', 'focus')
 * @param {string} params.elementType - Tipo de elemento (ej: 'button', 'link', 'input', 'dropdown')
 */
export const trackUIInteraction = ({ module, section, elementName, action, elementType }) => {
  const dl = ensureDataLayer();
  if (!dl) return;

  // Obtener module y section del contexto si no se proporcionan
  let resolvedModule = module;
  let resolvedSection = section;
  
  if (getAnalyticsContext) {
    try {
      const context = getAnalyticsContext();
      if (!resolvedModule && context?.currentModule) {
        resolvedModule = context.currentModule;
      }
      if (!resolvedSection && context?.currentSection) {
        resolvedSection = context.currentSection;
      }
    } catch (e) {
      // Si hay error al obtener contexto (ej: fuera del provider), continuar con valores explícitos
    }
  }

  // Validar que module esté disponible (del parámetro o del contexto)
  if (!resolvedModule || typeof resolvedModule !== 'string') {
    console.warn('[Analytics] trackUIInteraction: module debe ser un string no vacío. Proporciónalo explícitamente o asegúrate de que usePageViewTracker esté activo.');
    return;
  }

  if (!elementName || typeof elementName !== 'string') {
    console.warn('[Analytics] trackUIInteraction: elementName debe ser un string no vacío');
    return;
  }

  if (!action || typeof action !== 'string') {
    console.warn('[Analytics] trackUIInteraction: action debe ser un string no vacío');
    return;
  }

  if (!elementType || typeof elementType !== 'string') {
    console.warn('[Analytics] trackUIInteraction: elementType debe ser un string no vacío');
    return;
  }

  const event = {
    event: 'ui_interaction',
    module: resolvedModule,
    element_name: elementName,
    action,
    element_type: elementType,
    page_path: window.location?.pathname || '',
    page_location: window.location?.href || '',
  };

  // Incluir section si existe (del parámetro explícito o del contexto)
  if (resolvedSection && typeof resolvedSection === 'string') {
    event.section = resolvedSection;
  }

  // Incluir user_role desde window.polkoUserRole si está disponible
  if (window.polkoUserRole && typeof window.polkoUserRole === 'string') {
    event.user_role = window.polkoUserRole;
  }

  dl.push(event);
};

/**
 * Genera el nombre del elemento siguiendo la nomenclatura estándar:
 * {tipoDeElemento}__{componente}__{nombreSemantico}
 * 
 * @param {string} elementType - Tipo de elemento (ej: 'button', 'icon', 'card', 'link')
 * @param {string} component - Nombre del componente (ej: 'dashboard_statistics', 'quick_access')
 * @param {string} semantic - Nombre semántico del elemento (ej: 'edit', 'save', 'cancel')
 * @returns {string} Nombre del elemento formateado
 * 
 * @example
 * import { ANALYTICS_ELEMENT_TYPES, ANALYTICS_SECTIONS } from '../constants/analyticsConstants';
 * getElementName(ANALYTICS_ELEMENT_TYPES.BUTTON, ANALYTICS_SECTIONS.QUICK_ACCESS, 'save') // 'button__quick_access__save'
 * getElementName(ANALYTICS_ELEMENT_TYPES.ICON, ANALYTICS_SECTIONS.MY_WALLET, 'add_agreement') // 'icon__my_wallet__add_agreement'
 */
export const getElementName = (elementType, component, semantic) => {
  if (!elementType || !component || !semantic) {
    console.warn('[Analytics] getElementName: todos los parámetros (elementType, component, semantic) son requeridos');
    return '';
  }
  return `${elementType}__${component}__${semantic}`;
};

/**
 * Alias más corto y amigable para trackUIInteraction
 * Especialmente útil para clicks, que es el caso de uso más común
 * 
 * @param {Object} params - Parámetros de la interacción
 * @param {string} [params.module] - Módulo (opcional, usa el del contexto)
 * @param {string} [params.section] - Sección (opcional, usa la del contexto)
 * @param {string} params.elementName - Nombre del elemento
 * @param {string} [params.action] - Acción (default: 'click')
 * @param {string} [params.elementType] - Tipo de elemento (default: 'button')
 */
export const trackClick = ({ module, section, elementName, action = 'click', elementType = 'button' }) => {
  return trackUIInteraction({ module, section, elementName, action, elementType });
};

/**
 * Trackea un evento personalizado de GTM
 * Envía un evento personalizado al dataLayer que GTM puede procesar
 * 
 * @param {Object} params - Parámetros del evento personalizado
 * @param {string} params.eventName - Nombre del evento personalizado (ej: 'quote_response')
 */
export const trackCustomEvent = ({ eventName }) => {
  const dl = ensureDataLayer();
  if (!dl) return;

  if (!eventName || typeof eventName !== 'string') {
    console.warn('[Analytics] trackCustomEvent: eventName debe ser un string no vacío');
    return;
  }

  const event = {
    event: eventName
  };

  dl.push(event);
};
